/**
 * Neutral, mechanics-based identifiers. Display names, art and flavor come from
 * theme files — these IDs never appear in the UI.
 */
export const ROLES = ['financier', 'slayer', 'raider', 'broker', 'guardian'] as const;
export type RoleId = (typeof ROLES)[number];

export const ACTIONS = [
  'income',
  'foreign_aid',
  'strike',
  'tax',
  'assassinate',
  'steal',
  'exchange',
] as const;
export type ActionType = (typeof ACTIONS)[number];

export interface ActionSpec {
  cost: number;
  needsTarget: boolean;
  /** Role this action claims; undefined = unchallengeable general action. */
  claims?: RoleId;
  /** Roles whose claim can block this action. */
  blockableBy: RoleId[];
  /** Who may block: the target only, or anyone (foreign aid has no target). */
  blockedByAnyone?: boolean;
}

export const ACTION_SPECS: Record<ActionType, ActionSpec> = {
  income: { cost: 0, needsTarget: false, blockableBy: [] },
  foreign_aid: { cost: 0, needsTarget: false, blockableBy: ['financier'], blockedByAnyone: true },
  strike: { cost: 7, needsTarget: true, blockableBy: [] },
  tax: { cost: 0, needsTarget: false, claims: 'financier', blockableBy: [] },
  assassinate: { cost: 3, needsTarget: true, claims: 'slayer', blockableBy: ['guardian'] },
  steal: { cost: 0, needsTarget: true, claims: 'raider', blockableBy: ['raider', 'broker'] },
  exchange: { cost: 0, needsTarget: false, claims: 'broker', blockableBy: [] },
};

/**
 * Plain-language mechanic tips, identical across themes. The UI shows these next
 * to the themed name so a newcomer knows what a button actually does.
 */
export const ACTION_TIPS: Record<ActionType, string> = {
  income: 'Take 1 coin from the bank. Always safe — cannot be challenged or blocked.',
  foreign_aid: 'Take 2 coins from the bank. Anyone claiming the tax role can block it.',
  strike: 'Pay 7 coins to force a player to lose a card. Unstoppable — no block, no challenge.',
  tax: 'Take 3 coins. Claims the tax role, so it can be challenged.',
  assassinate: 'Pay 3 coins to force a player to lose a card. The target may claim the guard role to block.',
  steal: "Take 2 coins from a player. The target may block by claiming the thief or trade role.",
  exchange: 'Draw 2 cards from the deck, then keep the best of your hand and put 2 back.',
};

/**
 * What each hidden role lets you do, plain-language. Shown as a "cast" so players
 * learn which claims to fear before the game starts.
 */
export const ROLE_TIPS: Record<RoleId, string> = {
  financier: 'Take 3 coins (tax). Also blocks anyone taking foreign aid.',
  slayer: 'Pay 3 coins to make a player lose a card (assassinate).',
  raider: 'Steal 2 coins from a player. Also blocks being stolen from.',
  broker: 'Swap cards with the deck (exchange). Also blocks being stolen from.',
  guardian: 'Blocks an assassination against you. No action of its own.',
};

/** Coins at or above which the only legal action is `strike`. */
export const FORCED_STRIKE_AT = 10;
export const COPIES_PER_ROLE = 3;
export const STARTING_COINS = 2;
/** Official two-player tweak: the first player starts with one fewer coin. */
export const TWO_PLAYER_FIRST_COINS = 1;
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 6;
export const CARDS_PER_PLAYER = 2;
